<template>
    <VJoyTemplate
        :loading="isStepLoading"
        data-testid="profile-edition-experience-resume-import-step-experience-list"
        class="profile__resume-import__experience-list"
    >
        <template #template-title>
            {{ t('profile.experience.funnel.step.experience_list.title', {nbExperiences: resumeImportExperienceList.length || '0'}) }}
        </template>
        <template #template-subtitle>
            <VJoyHighlight v-if="experienceListInvalidCounter > 0" :accent="true" level="warning">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="t('profile.experience.funnel.step.experience_list.warning', {nbExperiences: experienceListInvalidCounter || '1'})" />
            </VJoyHighlight>
            <VJoyHighlight v-if="hasReachedMinimumExperiences" :accent="true" level="warning">
                {{ t('profile.experience.funnel.step.experience_list.experience.warning.limit') }}
            </VJoyHighlight>
        </template>
        <template #template-main>
            <template v-for="experience in resumeImportExperienceList" :key="`resume-import-experience--${experience.id}`">
                <Transition>
                    <ResumeImportExperienceComponent
                        v-if="!isExperienceInEditMode(experience)"
                        :experience="experience"
                        :is-enabled-cta-delete="!hasReachedMinimumExperiences"
                        :is-visible-cta-edit="true"
                        :is-visible-validation-warning="!experienceListValidationStatus[experience.id!].isValid"
                        @resume-import-experience:edit="handleResumeImportExperienceEdit"
                        @resume-import-experience:delete="handleResumeImportExperienceDelete"
                    />
                    <EditExperiencePanel
                        v-else
                        :experience="experienceToEdit!"
                        :skills-editable="true"
                        append-to-element=".profile__resume-import__experience-list"
                        @edit-experience:cancel="handleEditExperienceCancel"
                        @edit-experience:save="handleEditExperienceSave"
                    >
                        <template #edit-form-title-action>
                            <ResumeImportExperienceSourceComponent
                                v-if="experience.source"
                                :experience-id="experience.id"
                                :experience-source="experience.source"
                                data-testid="profile-edition-experience-resume-import-step-experience-list-source"
                            />
                        </template>
                    </EditExperiencePanel>
                </Transition>
            </template>
        </template>
        <template #template-loader-content>{{ t('profile.experience.funnel.step.generic.loader.label') }}</template>
    </VJoyTemplate>
</template>
<script setup lang="ts">
    import {computed, onMounted, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useDataTracking, useTranslation} from '#imports';
    import {VJoyHighlight, VJoyTemplate} from '@maltjoy/core-vue';
    import {eventBus} from '~/config/events';
    import {ResumeImportStep, TrackingEvents} from '~/config/TrackingEvents';
    import type {EditExperience} from '~/features/experience/types/EditExperience';
    import EditExperiencePanel from '~/features/experience/ui/edition/EditExperiencePanel.vue';
    import ResumeImportExperienceComponent from '~/features/resume-import/ui/ResumeImportExperience.vue';
    import ResumeImportExperienceSourceComponent from '~/features/resume-import/ui/ResumeImportExperienceSource.vue';
    import {useRulesStore} from '~/store/rules/rulesStore';
    import {useDefineFunnelNextStepHandler} from '../../composable/useDefineFunnelNextStepHandler';
    import {useResumeImportStore} from '../../store/useResumeImportStore';
    import type {ResumeImportExperience} from '../../types/ResumeImportExperience';

    const {t} = useTranslation();
    const {track} = useDataTracking();
    const storeResumeImport = useResumeImportStore();
    const {updateExistingExperience, updateExperienceList} = storeResumeImport;
    const {isFunnelCtaNextEnabled, resumeImportExperienceList} = storeToRefs(storeResumeImport);
    useDefineFunnelNextStepHandler(stepNextAction);

    const rulesStore = useRulesStore();

    const experienceToEdit = ref<EditExperience>();
    const isStepLoading = ref<boolean>(false);
    const hasEditionBeenOpened = ref<boolean>(false);

    const experienceListValidationStatus = computed(() => {
        const validationStatus: Record<string, {isValid: boolean}> = {};

        resumeImportExperienceList.value.forEach(
            (experience) => (validationStatus[experience.id!] = {isValid: rulesStore.isValidExperienceRequest(experience)}),
        );

        return validationStatus;
    });
    const experienceListInvalidCounter = computed(() => {
        return Object.values(experienceListValidationStatus.value).filter((experience) => !experience.isValid).length;
    });
    const hasReachedMinimumExperiences = computed(() => resumeImportExperienceList.value.length <= 1);

    watch(
        [experienceListInvalidCounter, experienceToEdit],
        () => {
            isFunnelCtaNextEnabled.value = !experienceToEdit.value && experienceListInvalidCounter.value === 0;
        },
        {deep: true},
    );

    onMounted(() => {
        isFunnelCtaNextEnabled.value = !experienceToEdit.value && experienceListInvalidCounter.value === 0;
        track(TrackingEvents.RESUME_IMPORT_FUNNEL, {funnel_step: ResumeImportStep.EXPERIENCE_LIST});
    });

    function isExperienceInEditMode(experience: ResumeImportExperience): boolean {
        return experienceToEdit.value?.id === experience.id;
    }

    function handleEditExperienceCancel(): void {
        experienceToEdit.value = undefined;
    }

    function handleEditExperienceSave(experienceToSave: EditExperience): void {
        updateExistingExperience(experienceToSave);

        // experienceToEdit.value = undefined;
        experienceToEdit.value = undefined;
    }

    function handleResumeImportExperienceDelete(experience: ResumeImportExperience): void {
        resumeImportExperienceList.value = resumeImportExperienceList.value.filter((item) => item.id && item.id !== experience.id);
    }

    function handleResumeImportExperienceEdit(experience: ResumeImportExperience): void {
        // resumeImportExperienceList.value = resumeImportExperienceList.value.filter((item) => item.id && item.id !== experience.id);
        // TODO convert xp
        experienceToEdit.value = experience as EditExperience;
        hasEditionBeenOpened.value = true;
    }

    async function stepNextAction(): Promise<void> {
        try {
            isStepLoading.value = true;
            if (experienceListInvalidCounter.value === 0) {
                await updateExperienceList();
                eventBus.emit('resume-import:updated');
            } else {
                throw new Error('Experience list is not valid');
            }
        } finally {
            isStepLoading.value = false;
            track(TrackingEvents.RESUME_IMPORT_FUNNEL, {
                funnel_step: ResumeImportStep.IMPORT_DONE,
                work_experience_edition_opened: hasEditionBeenOpened.value,
            });
        }
    }
</script>
<style lang="scss">
    .profile__resume-import__experience-list {
        .joy-template-slot-subtitle > *:not(:last-of-type) {
            margin-bottom: var(--joy-core-spacing-2);
        }
    }
</style>
